/**
 * Shared classifier for Odoo POS order lines.
 *
 * `odoo_purchases.lines` holds every line of every POS order — including
 * memberships and PT packs that were rung up at the till alongside true
 * café / retail items. This module is the single source of truth for deciding
 * what counts as "F&B / retail" vs. a membership or PT pack, so the Finance
 * tab's revenue dedup (`useDailyRevenueLast30Days`) and the F&B report agree
 * on the same definition and can't drift apart when the Odoo menu changes.
 *
 * "F&B" here means everything that is NOT a membership and NOT a PT pack —
 * i.e. `classifyOdooLine(name) === 'other'` (café, drinks, water, merch).
 */

/**
 * Shape of one row inside the `odoo_purchases.lines` JSONB array. Only the
 * fields the reports consume are typed. `price_subtotal_incl` is the line
 * total INCLUDING PB1 + service charge, and may arrive as a string from PG.
 */
export interface OdooPurchaseLine {
  qty?: number;
  product_name: string;
  price_subtotal_incl: number | string;
  /**
   * POS display category from Odoo (`pos.category`), e.g. "Coffee" /
   * "Smoothies" / "Water". Captured by the purchase sync since 2026-06; older
   * rows have it `undefined` until the next full re-sync backfills them. The
   * meaningful field for distinguishing café items (coffee vs protein shake).
   */
  pos_category?: string | null;
  /** Internal product category (`product.category`); fallback to `pos_category`. */
  category?: string | null;
}

/**
 * Fallback classifier by product name, for legacy rows synced before the
 * `category` field was captured (older POS pulls have it `undefined`). Biased
 * toward over-classifying as non-retail — any membership / PT / gift-card-shaped
 * name is dropped from F&B; the canonical revenue for those lives in their
 * internal tables (`memberships`, `pt_session_packs`), not the POS lines.
 *
 * Known product names in production (May 2026):
 *   - "Personal Trainer (12-Sessions)", "Personal Trainer (6-Sessions)" → pt_pack
 *   - "Membership (1-Year)", "Membership (4-Weeks)"                     → membership
 *   - "Complimentary & Barter (1-Week|4-Weeks)"                         → membership
 *     (comp passes; line subtotal is 0 so it's purely cosmetic)
 *   - "Gift Card Membership"                                            → membership
 *   - "Mineral Water (...)", café / merch                              → other (F&B)
 */
function classifyByName(productName: string): 'membership' | 'pt_pack' | 'other' {
  const n = (productName ?? '').toLowerCase();
  if (n.startsWith('personal trainer')) return 'pt_pack';
  if (n.startsWith('membership')) return 'membership';
  if (n.startsWith('gift card')) return 'membership';
  if (
    n.startsWith('complimentary & barter') ||
    n.startsWith('complimentary and barter')
  ) {
    return 'membership';
  }
  return 'other';
}

/**
 * Classify an Odoo POS line so memberships, PT packs and gift cards can be
 * excluded from the "retail / F&B" bucket.
 *
 * **Primary signal is the product's internal `category`** (`product.category`) —
 * a stable two-level hierarchy that Odoo assigns regardless of how a product is
 * named:
 *   - `Gym / Fitness`     → PT packs                    → `pt_pack`
 *   - `Gym / Memberships` → memberships & comp passes   → `membership`
 *   - `Gym / Gift Card`   → stored-value gift cards     → `membership` (not F&B)
 *   - `F&B / …`           → café / drinks / water       → `other`
 *   - `Merchandise / …`   → apparel / accessories       → `other` (retail)
 *
 * Anything under `Gym /` is never F&B. This replaces the old name-prefix-only
 * logic, which booked "Gift Card Membership" (Rp 36.96M, May 2026) as F&B because
 * the name starts with "gift card", not "membership". `category` is the field the
 * POS already tags correctly, so new products can't silently leak into F&B when
 * the menu changes. Rows without a `category` (legacy syncs) fall back to
 * `classifyByName`.
 */
export function classifyOdooLine(
  line: OdooPurchaseLine,
): 'membership' | 'pt_pack' | 'other' {
  const category = (line.category ?? '').trim().toLowerCase();
  if (category.startsWith('gym /')) {
    // PT packs vs memberships/gift-cards; F&B only cares that it's not `other`,
    // but keep the distinction accurate for any future consumer.
    return category.includes('fitness') ? 'pt_pack' : 'membership';
  }
  if (category) return 'other'; // F&B / … or Merchandise / … → retail
  return classifyByName(line.product_name); // legacy row, no category captured
}

/** True when a POS line counts as F&B / retail (not a membership, PT pack or gift card). */
export function isFnbLine(line: OdooPurchaseLine): boolean {
  return classifyOdooLine(line) === 'other';
}

/**
 * True when a POS line is apparel/merch (Odoo `Merchandise / …` category) — used
 * by the Inventory dashboard's sales side to isolate merch from café/F&B inside
 * the same "other" retail bucket. Legacy rows with no `category` (synced before
 * it was captured) return false: better to under-count merch than to mis-tag a
 * café item as apparel. The purchase syncs re-pull and backfill `category`.
 */
export function isMerchLine(line: OdooPurchaseLine): boolean {
  return (line.category ?? '').trim().toLowerCase().startsWith('merchandise');
}

/** Restaurant sales mix, as split by the F&B "Daily Sales" report. */
export type FnbSubCategory = 'food' | 'beverage' | 'alcohol';

/**
 * POS categories (`pos.category`) that count as alcohol.
 *
 * Odoo has NO `F&B / Alcohol` product category — alcohol sits inside
 * `F&B / Beverage` and is only distinguishable by its POS category. Validated
 * against the hand-kept restaurant spreadsheet: this exact list reproduced its
 * July 2026 alcohol column to the rupiah (Rp 17,193,080) on all 16 trading days.
 *
 * Note "Mocktails" is deliberately absent — they're non-alcoholic and belong in
 * beverage. Exported so the UI explainer and the tests read the same list the
 * classifier does and can't drift from it.
 */
export const ALCOHOL_POS_CATEGORIES = [
  'Alcohol',
  'Cocktails',
  'Wine',
  'Beer & Seltzer',
] as const;

/**
 * POS categories used to bucket lines whose internal `category` is missing.
 *
 * This is a SAFETY NET, not the fix. Products should carry an `F&B / …`
 * category in Odoo; when one doesn't, the line would otherwise fall through to
 * `null` and silently vanish from the grid, breaking the report's footing
 * invariant (Food + Beverage + Alcohol = Net Sales). The real fix is to assign
 * the category in Odoo — the syncs re-pull and upsert `lines` in place, so
 * doing so self-heals every historical row on the next run and makes these
 * lists dead code.
 *
 * So that a lapse can't hide here, `aggregateDailySales` counts the lines that
 * land in this fallback and the Daily Sales tab flags the day as
 * "Uncategorized product".
 */
const FALLBACK_FOOD_POS_CATEGORIES = ['Sides', 'Add Ons'] as const;
const FALLBACK_BEVERAGE_POS_CATEGORIES = [
  'Water & Soda',
  'Electrolite & Healthy Drinks',
] as const;

const norm = (v: string | null | undefined): string =>
  (v ?? '').trim().toLowerCase();
const setOf = (xs: readonly string[]): Set<string> => new Set(xs.map(norm));

const ALCOHOL_SET = setOf(ALCOHOL_POS_CATEGORIES);
const FALLBACK_FOOD_SET = setOf(FALLBACK_FOOD_POS_CATEGORIES);
const FALLBACK_BEVERAGE_SET = setOf(FALLBACK_BEVERAGE_POS_CATEGORIES);

/**
 * Split a POS line into the restaurant sales mix: food, beverage or alcohol.
 * Returns `null` for anything that isn't a restaurant line (merchandise,
 * memberships, PT packs) — those are excluded from the Daily Sales grid, which
 * only foots if Net Sales is exactly Food + Beverage + Alcohol.
 *
 * Note this is a narrower question than `isFnbLine`, which means "not a
 * membership and not a PT pack" and therefore lets merchandise through.
 *
 * Precedence is deliberate:
 *   1. Alcohol wins on `pos_category` alone, BEFORE any category check — the
 *      production `Alcohol` lines carry no internal category at all, and a Wine
 *      product tagged `F&B / Beverage` must still be booked as alcohol.
 *   2. `F&B / Beverage` → beverage.
 *   3. Any other `F&B / …` → food (residual, see below).
 *   4. No category → fall back to `pos_category` (the safety net above).
 *
 * Step 3 treats food as the RESIDUAL rather than exact-matching `F&B / Food`.
 * If someone adds `F&B / Bakery` in Odoo tomorrow, exact-matching would make
 * those sales silently disappear from the report; bucketing them as food
 * mislabels them instead. A visible error beats an invisible one.
 */
export function classifyFnbLine(line: OdooPurchaseLine): FnbSubCategory | null {
  const posCategory = norm(line.pos_category);
  if (ALCOHOL_SET.has(posCategory)) return 'alcohol';

  const category = norm(line.category);
  if (category === 'f&b / beverage') return 'beverage';
  if (category.startsWith('f&b /')) return 'food';
  if (category) return null; // Merchandise / … or Gym / … — not a restaurant line

  if (FALLBACK_FOOD_SET.has(posCategory)) return 'food';
  if (FALLBACK_BEVERAGE_SET.has(posCategory)) return 'beverage';
  return null;
}

/**
 * True when a line's food/beverage bucket was *inferred* from `pos_category`
 * because the product has no `F&B / …` category in Odoo. Surfaced per-day in the
 * report so the data-hygiene gap is visible rather than silently absorbed.
 *
 * Alcohol is deliberately NOT included, even though those products are missing a
 * category too. Alcohol is matched on `pos_category` at step 1 of
 * `classifyFnbLine`, before any category check — so for a Negroni the missing
 * category changes nothing and the figure is exactly right. Flagging it would
 * imply doubt about a number that isn't in doubt. Only the step-4 guesses, where
 * a drifting list really could misfile or drop the line, are worth surfacing.
 */
export function isFallbackClassified(line: OdooPurchaseLine): boolean {
  if (norm(line.category)) return false;
  const posCategory = norm(line.pos_category);
  return (
    FALLBACK_FOOD_SET.has(posCategory) || FALLBACK_BEVERAGE_SET.has(posCategory)
  );
}

/**
 * True when a line carries no usable category signal at all and was therefore
 * dropped from the grid entirely — its takings are missing from Net Sales.
 *
 * This is the dangerous case the other flags don't cover. Merchandise and
 * memberships also classify as `null`, but they say what they are (`Merchandise
 * / …`, `Gym / …`) and excluding them is correct. A line with NO internal
 * category that also matched no POS rule is simply unknown, and silently
 * dropping it makes the day under-report with nothing to show for it.
 *
 * Production has exactly one such product today — a duplicate "Negroni"
 * (template 1960) that is POS-enabled with neither category set and has never
 * been rung up. If a cashier ever picks it, this is what makes that visible.
 */
export function isUnclassifiedFnbLine(line: OdooPurchaseLine): boolean {
  return classifyFnbLine(line) === null && !norm(line.category);
}

/**
 * A POS line's `price_subtotal_incl` with the sign a refund actually deserves.
 *
 * Odoo writes a refund as its own `pos.order` whose lines carry a NEGATIVE
 * `qty` but a POSITIVE `price_subtotal_incl` (the order's `amount_total` is
 * negative; the line amounts are not). Verified on production order
 * "Main Reception - 000002 REFUND" (Odoo id 1988): qty -1, subtotal 173,250,
 * amount_total -173,250. Summing raw line subtotals therefore counts a refunded
 * item twice — once when sold and again when returned.
 *
 * This flips the subtotal negative when the quantity is negative and the amount
 * is positive, and leaves every other shape alone (paid lines, Rp 0 comps and
 * comp reversals, or a line that already arrived negative). Used by the Merch
 * report and the Inventory dashboard; the F&B report deliberately still sums
 * unsigned because its Daily Sales grid was validated against the restaurant
 * spreadsheet with that arithmetic (see docs/MERCH_REPORT_SPEC.md).
 */
export function signedLineSubtotal(line: OdooPurchaseLine): number {
  const parsed = Number(line.price_subtotal_incl);
  const subtotal = Number.isFinite(parsed) ? parsed : 0;
  const qty = Number(line.qty) || 0;
  return qty < 0 && subtotal > 0 ? -subtotal : subtotal;
}
